import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ArrowRight, CheckCircle, Download, Phone, Mail, MapPin, Anchor, Wrench, Ship, Cog } from "lucide-react";

const Index = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    // Handle form submission here
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0 bg-[url('/images/offshore_terminal_1.png')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              Engineering Solutions for <span className="text-orange-400">Marine, Energy & Industrial</span> Operations
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 text-blue-100 px-4">
              Delivering reliable marine hoses, flexible industrial hoses, pig launcher systems, and ship brokering services across Malaysia and Southeast Asia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg">
                Request Quotation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 text-lg">
                <Download className="mr-2 h-5 w-5" />
                Download Company Profile
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Trusted by Marine & Energy Operators</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: CheckCircle, text: "Quality-Assured Products" },
              { icon: Phone, text: "Expert Technical Support" },
              { icon: CheckCircle, text: "Fast Response" },
              { icon: MapPin, text: "Southeast Asia Coverage" },
              { icon: CheckCircle, text: "15+ Years Experience" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <item.icon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Solutions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Core Solutions</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive engineering solutions for marine and industrial operations
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Floating Marine Hose Systems */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-yellow-200 transition-colors">
                  <Anchor className="h-8 w-8 text-yellow-600" />
                </div>
                <Badge className="bg-yellow-500 text-white mb-2">🟡</Badge>
                <CardTitle className="text-xl">Floating Marine Hose Systems</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center mb-4">
                  Engineered for offshore terminals, SBM & CBM systems, FPSO/FSO offloading operations. High durability, GMPHOM compliance, and complete hose string supply.
                </CardDescription>
                <Button variant="outline" className="w-full group-hover:bg-yellow-50">
                  Learn More
                </Button>
              </CardContent>
            </Card>

            {/* Flexible Industrial Hose */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                  <Wrench className="h-8 w-8 text-green-600" />
                </div>
                <Badge className="bg-green-500 text-white mb-2">🟢</Badge>
                <CardTitle className="text-xl">Flexible Industrial Hose</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center mb-4">
                  Full range of oil, chemical, and industrial transfer hoses for refineries, factories, and petrochemical facilities.
                </CardDescription>
                <Button variant="outline" className="w-full group-hover:bg-green-50">
                  View Products
                </Button>
              </CardContent>
            </Card>

            {/* Pig Launcher & Receiver Systems */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-200 transition-colors">
                  <Cog className="h-8 w-8 text-gray-600" />
                </div>
                <Badge className="bg-gray-500 text-white mb-2">⚙️</Badge>
                <CardTitle className="text-xl">Pig Launcher & Receiver Systems</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center mb-4">
                  Custom-designed launcher & receiver units for pipeline maintenance and commissioning. Includes pigging accessories and spare parts.
                </CardDescription>
                <Button variant="outline" className="w-full group-hover:bg-gray-50">
                  Explore Solutions
                </Button>
              </CardContent>
            </Card>

            {/* Ship Brokering & Marine Agent */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                  <Ship className="h-8 w-8 text-blue-600" />
                </div>
                <Badge className="bg-blue-500 text-white mb-2">🚢</Badge>
                <CardTitle className="text-xl">Ship Brokering & Marine Agent</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center mb-4">
                  Vessel sourcing, chartering, tug & barge arrangement, OSV support, bunker coordination.
                </CardDescription>
                <Button variant="outline" className="w-full group-hover:bg-blue-50">
                  Marine Services
                </Button>
              </CardContent>
            </Card>

            {/* Valve Products */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
                  <Cog className="h-8 w-8 text-purple-600" />
                </div>
                <Badge className="bg-purple-500 text-white mb-2">⚙️</Badge>
                <CardTitle className="text-xl">Valve Products</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center mb-4">
                  Comprehensive valve solutions including ball, gate, globe, and butterfly valves for oil & gas operations.
                </CardDescription>
                <Button variant="outline" className="w-full group-hover:bg-purple-50">
                  View Valves
                </Button>
              </CardContent>
            </Card>

            {/* Process Skid Packages */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-200 transition-colors">
                  <Wrench className="h-8 w-8 text-indigo-600" />
                </div>
                <Badge className="bg-indigo-500 text-white mb-2">🏭</Badge>
                <CardTitle className="text-xl">Process Skid Packages</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center mb-4">
                  Modular process systems including metering, filtration, and chemical injection skids for fast deployment.
                </CardDescription>
                <Button variant="outline" className="w-full group-hover:bg-indigo-50">
                  Explore Skids
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose G5 Petroleum */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Why Choose G5 Petroleum</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "15+ Years Combined Marine & Industrial Experience",
                description: "Deep expertise in marine and industrial operations across Southeast Asia"
              },
              {
                title: "South East Asia Presence",
                description: "Strategic locations and partnerships across Malaysia, Singapore, Indonesia, Vietnam, and Thailand"
              },
              {
                title: "Strong Network With Global Manufacturers",
                description: "Direct partnerships with leading manufacturers ensuring quality and competitive pricing"
              },
              {
                title: "Fast Delivery & Competitive Pricing",
                description: "Efficient supply chain management for quick turnaround times"
              },
              {
                title: "Engineering-Focused Support",
                description: "Technical expertise and consultation for complex marine and industrial projects"
              },
              {
                title: "Quality Assurance",
                description: "All products meet international standards including GMPHOM compliance"
              }
            ].map((item, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{item.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Inquiry Form */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Quick Inquiry Form</h2>
              <p className="text-lg sm:text-xl text-blue-100 px-4">Get in touch with our engineering team for your marine and industrial needs</p>
            </div>
            
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-white">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/70"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="company" className="text-white">Company *</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleInputChange("company", e.target.value)}
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/70"
                      placeholder="Company name"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-white">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/70"
                      placeholder="your.email@company.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-white">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/70"
                      placeholder="+60 xxx xxx xxxx"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="service" className="text-white">Product/Service Interest</Label>
                    <Select value={formData.service} onValueChange={(value) => handleInputChange("service", value)}>
                      <SelectTrigger className="bg-white/20 border-white/30 text-white">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marine-hose">Floating Marine Hose Systems</SelectItem>
                        <SelectItem value="industrial-hose">Flexible Industrial Hose</SelectItem>
                        <SelectItem value="pig-launcher">Pig Launcher & Receiver Systems</SelectItem>
                        <SelectItem value="ship-brokering">Ship Brokering & Marine Agent</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="message" className="text-white">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/70 min-h-[120px]"
                      placeholder="Please describe your requirements, project details, or any specific questions..."
                    />
                  </div>
                  
                  <div className="md:col-span-2 text-center">
                    <Button type="submit" size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-12 py-4 text-lg">
                      Submit Inquiry
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Index;
